import * as Yup from 'yup';
import { GooglePlacesService } from '../../../services/googlePlaces.service';
import { postalCodeFormatMap } from '../../../config/countries';
import { zipCodeMaskToRegex } from '../../../helper/helper';

export const validationStudentBookSchema = Yup.object().shape({
  shippingEmail: Yup.string().email('Invalid email address').required('Email is required'),
});

// Enhanced validation schema with Google Places API integration
export const enhancedValidationSchema = Yup.object()
  .shape({
    shippingEmail: Yup.string().email('Invalid email address').required('Email is required'),
    customerName: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be less than 50 characters')
      .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
    recipientName: Yup.string().when(['readonlyShippingAddress'], {
      is: (readonlyShippingAddress: boolean) => readonlyShippingAddress,
      then: (schema) =>
        schema
          .required("Recipient's name is required")
          .min(2, 'Name must be at least 2 characters')
          .max(50, 'Name must be less than 50 characters')
          .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
      otherwise: (schema) => schema.optional(),
    }),
    shippingAddress1: Yup.string()
      .required('Address is required')
      .min(5, 'Address must be at least 5 characters')
      .max(250, 'Address must be less than 250 characters'),
    shippingAddress2: Yup.string()
      .max(250, 'Address must be less than 250 characters')
      .matches(/^[a-zA-Z0-9\s,.\-#]*$/, 'Address contains invalid characters'),
    shippingState: Yup.string()
      .required('State is required')
      .min(2, 'State must be at least 2 characters')
      .max(50, 'State must be less than 50 characters'),
    shippingCity: Yup.string()
      .required('City is required')
      .min(2, 'City must be at least 2 characters')
      .max(50, 'City must be less than 50 characters')
      .matches(/^[a-zA-Z\s'-]+$/, 'City can only contain letters, spaces, hyphens, and apostrophes'),
    shippingCountry: Yup.string().required('Country is required'),
    shippingZip: Yup.string()
      .required('Zip code is required')
      .test('country-zip-mask', async function (value) {
        const { shippingCountry } = this.parent;
        const format = postalCodeFormatMap[shippingCountry];
        if (!value) return true;
        if (format.mask) {
          const regex = zipCodeMaskToRegex(format.mask);
          if (!regex.test(value)) {
            return this.createError({ message: `Zip code must match format: ${format.example}` });
          }
        }
        const countryCode = format.code || 'US';
        try {
          const isValid = await GooglePlacesService.validatePostalCode(value, countryCode);
          if (!isValid) {
            return this.createError({ message: `Invalid postal code for ${shippingCountry}` });
          }
          return isValid;
        } catch {
          return this.createError({ message: 'Error validating postal code' });
        }
      }),
    billingCardHolderName: Yup.string().when(['isBillingSameAsShipping'], {
      is: (isBillingSameAsShipping: boolean) => !isBillingSameAsShipping,
      then: (schema) =>
        schema
          .required('Name is required')
          .min(2, 'Name must be at least 2 characters')
          .max(50, 'Name must be less than 50 characters')
          .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
      otherwise: (schema) => schema.optional(),
    }),
    billingAddress1: Yup.string().when(['isBillingSameAsShipping'], {
      is: (isBillingSameAsShipping: boolean) => !isBillingSameAsShipping,
      then: (schema) =>
        schema
          .required('Address is required')
          .min(5, 'Address must be at least 5 characters')
          .max(250, 'Address must be less than 250 characters'),
      otherwise: (schema) => schema.optional(),
    }),
    billingAddress2: Yup.string().when(['isBillingSameAsShipping'], {
      is: (isBillingSameAsShipping: boolean) => !isBillingSameAsShipping,
      then: (schema) =>
        schema
          .max(250, 'Address must be less than 250 characters')
          .matches(/^[a-zA-Z0-9\s,.\-#]*$/, 'Address contains invalid characters'),
      otherwise: (schema) => schema.optional(),
    }),
    billingCity: Yup.string().when(['isBillingSameAsShipping'], {
      is: (isBillingSameAsShipping: boolean) => !isBillingSameAsShipping,
      then: (schema) =>
        schema
          .required('City is required')
          .min(2, 'City must be at least 2 characters')
          .max(50, 'City must be less than 50 characters')
          .matches(/^[a-zA-Z\s'-]+$/, 'City can only contain letters, spaces, hyphens, and apostrophes'),
      otherwise: (schema) => schema.optional(),
    }),
    billingPostalCode: Yup.string().when(['isBillingSameAsShipping'], {
      is: (isBillingSameAsShipping: boolean) => !isBillingSameAsShipping,
      then: (schema) =>
        schema.required('Zip code is required').test('country-zip-mask', async function (value) {
          const { shippingCountry } = this.parent;
          const format = postalCodeFormatMap[shippingCountry];
          if (!value) return true;
          if (format.mask) {
            const regex = zipCodeMaskToRegex(format.mask);
            if (!regex.test(value)) {
              return this.createError({ message: `Zip code must match format: ${format.example}` });
            }
          }
          const countryCode = format.code || 'US';
          try {
            const isValid = await GooglePlacesService.validatePostalCode(value, countryCode);
            if (!isValid) {
              return this.createError({ message: `Invalid postal code for ${shippingCountry}` });
            }
            return isValid;
          } catch {
            return this.createError({ message: 'Error validating postal code' });
          }
        }),
      otherwise: (schema) => schema.optional(),
    }),
    billingState: Yup.string().when(['isBillingSameAsShipping'], {
      is: (isBillingSameAsShipping: boolean) => !isBillingSameAsShipping,
      then: (schema) =>
        schema
          .required('State is required')
          .min(2, 'State must be at least 2 characters')
          .max(50, 'State must be less than 50 characters'),
      otherwise: (schema) => schema.optional(),
    }),
  })
  .test('is-valid-full-address', 'Please enter a valid address', async function (obj) {
    const { shippingAddress1, shippingCountry, shippingAddress2, shippingCity, shippingState, shippingZip } = obj;

    if (!shippingAddress1 || shippingAddress1.length < 5) return true;

    const countryCode = postalCodeFormatMap[shippingCountry || 'United States']?.code || 'US';

    const fullAddress = [shippingAddress1, shippingAddress2, shippingCity, shippingState, shippingZip]
      .filter(Boolean)
      .map((item) => item?.toString().trim())
      .filter(Boolean)
      .join(', ');

    const isValid = await GooglePlacesService.validateAddress(fullAddress, countryCode);

    return isValid
      ? true
      : this.createError({
          path: 'shippingAddress1',
          message: 'Address is not valid',
        });
  });
